import { formatISO } from 'date-fns';

import type { Amount } from '@skippay/core/@types/amount';
import { toFloat } from '@skippay/core/utils/amountUtils';
import { QueryFilter } from '@skippay/core/utils/apiUtils';
import { Paginated } from '@skippay/core/utils/queryUtils';
import { Maybe } from '@skippay/core/utils/types';

import { toIsoDate } from 'jarabot/utils';
import MyContractTransactionsQuery from 'queries/MyContractTransactionsQuery.graphql';
import OutagesQuery from 'queries/OutagesQuery.graphql';

import { LIST_CAPS } from '../../constants';
import { OutageItem, TransactionItem } from '../../types';
import { DomainSource } from '../types';

interface TransactionRow {
	amount?: Maybe<Amount>;
	postingDate?: Maybe<string>;
	createdDate?: Maybe<string>;
	type?: Maybe<string>;
	billing?: { month?: Maybe<string> } | null;
	additionalInfoCard?: { merchantName?: Maybe<string> } | null;
	additionalInfoApplication?: { partnerName?: Maybe<string> } | null;
}

export const transactionsSource: DomainSource<{
	myContractTransactions?: Paginated<TransactionRow>;
}> = {
	domains: ['transactions'],
	query: MyContractTransactionsQuery,
	variables: { limit: LIST_CAPS.transactions },
	toModel: (data) => {
		const rows = data.myContractTransactions?.data ?? [];
		const transactions: TransactionItem[] = rows
			.slice(0, LIST_CAPS.transactions)
			.map((transaction) => ({
				amount: transaction.amount ? toFloat(transaction.amount) : undefined,
				date: toIsoDate(transaction.postingDate ?? transaction.createdDate),
				type: transaction.type ?? undefined,
				billing: transaction.billing?.month ?? undefined,
				merchant:
					transaction.additionalInfoApplication?.partnerName ??
					transaction.additionalInfoCard?.merchantName ??
					undefined,
			}));
		return { transactions };
	},
};

interface OutageRow {
	affectedSystems?: Maybe<string[]>;
	endDatetime?: Maybe<string>;
}

export const outagesSource: DomainSource<{ outages?: Paginated<OutageRow> }> = {
	domains: ['outages'],
	query: OutagesQuery,
	getVariables: () => ({
		filter: new QueryFilter()
			.prop('startDatetime')
			.lt(formatISO(new Date()))
			.prop('endDatetime')
			.gt(formatISO(new Date()))
			.toString(),
	}),
	toModel: (data) => {
		const rows = data.outages?.data ?? [];
		const outages: OutageItem[] = rows.map((outage) => ({
			system: outage.affectedSystems?.[0] ?? undefined,
			endDate: toIsoDate(outage.endDatetime),
		}));
		return { outages };
	},
};
